<template>
  <div class="mx-auto max-w-7xl pt-4 pb-24 overflow-hidden lg:relative">
    <div class="mt-6 px-4 mx-auto sm:max-w-xl">
      <h1 class="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">
        How's It Going?
      </h1>
      <div class="relative mt-6">
        <p class="text-xl text-gray-500">
          It's been {{ surveyTypeLabel }} since {{ teamName }} started using WrestlingIQ.
          This 2-minute check-in helps us make sure things are working for you.
        </p>
        <p class="text-sm text-gray-400 mt-2">Our team personally reads every response.</p>
      </div>
    </div>

    <ValidationObserver ref="observer" v-slot="{ handleSubmit, failed }">
      <div class="sm:max-w-xl mx-auto">
        <div class="px-4">
          <form class="mt-8 grid grid-cols-1 gap-y-6">

            <!-- Goal Achievement Section -->
            <section class="bg-gray-50 border border-gray-200 shadow-sm rounded-lg px-4 py-6 sm:p-6 lg:p-8">
              <h2 class="text-lg font-medium text-gray-900">Goal Progress</h2>

              <div v-if="originalGoal" class="mt-4 bg-white border border-gray-200 rounded-lg p-4">
                <p class="text-sm text-gray-500 mb-1">Your goal when you signed up:</p>
                <p class="text-gray-900 font-medium">{{ originalGoal }}</p>
              </div>

              <div class="mt-6">
                <label class="block text-sm font-medium text-gray-700 mb-3">Have you achieved this goal?</label>
                <div class="space-y-2">
                  <label v-for="option in goalOptions" :key="option.value" class="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      v-model="form.goalAchieved"
                      :value="option.value"
                      :data-cy="'goal-achieved-' + option.label.toLowerCase()"
                      class="h-4 w-4 text-wiq-green focus:ring-wiq-green border-gray-300"
                    >
                    <span class="ml-3 text-gray-700">{{ option.label }}</span>
                  </label>
                </div>
              </div>

              <div class="mt-6">
                <label class="block text-sm font-medium text-gray-700 mb-3">On a scale of 1-5, how much progress have you made?</label>
                <div class="flex gap-4">
                  <label v-for="n in 5" :key="n" class="flex flex-col items-center cursor-pointer">
                    <input
                      type="radio"
                      v-model="form.goalProgress"
                      :value="n"
                      :data-cy="'goal-progress-' + n"
                      class="h-4 w-4 text-wiq-green focus:ring-wiq-green border-gray-300"
                    >
                    <span class="mt-1 text-sm text-gray-600">{{ n }}</span>
                  </label>
                </div>
                <p class="mt-2 text-xs text-gray-500">1 = No progress, 5 = Fully achieved</p>
              </div>

              <div class="mt-6">
                <label for="goal-notes" class="block text-sm font-medium text-gray-700">Any notes on your progress?</label>
                <div class="mt-1">
                  <textarea
                    id="goal-notes"
                    data-cy="goal-notes"
                    v-model="form.goalNotes"
                    rows="3"
                    :class="inputClasses()"
                    placeholder="Share any context about your progress..."
                  ></textarea>
                </div>
              </div>
            </section>

            <!-- Admin Hours Section -->
            <section class="bg-gray-50 border border-gray-200 shadow-sm rounded-lg px-4 py-6 sm:p-6 lg:p-8">
              <h2 class="text-lg font-medium text-gray-900">Your Time</h2>

              <div v-if="originalAdminHours" class="mt-4 bg-white border border-gray-200 rounded-lg p-4">
                <p class="text-sm text-gray-500 mb-1">When you signed up, you estimated spending:</p>
                <p class="text-gray-900 font-medium">{{ originalAdminHours }} hours/week on admin tasks</p>
              </div>

              <div class="mt-6">
                <ValidationProvider name="admin hours" rules="required" v-slot="{ errors }">
                  <label for="admin-hours" class="block text-sm font-medium text-gray-700">
                    How many hours do you spend now on admin tasks per week?
                  </label>
                  <div class="mt-1 relative rounded-md shadow-sm" style="max-width: 150px;">
                    <input
                      type="number"
                      id="admin-hours"
                      v-model="form.adminHoursNow"
                      min="0"
                      max="100"
                      data-cy="admin-hours-input"
                      :class="inputClasses(errors[0])"
                    >
                    <error-icon v-if="errors[0]"></error-icon>
                  </div>
                  <p class="mt-2 text-sm text-red-600" v-if="errors[0]">{{ errors[0] }}</p>
                  <p v-if="hoursSaved !== null" class="mt-2 text-sm" :class="hoursSaved > 0 ? 'text-green-600' : 'text-gray-500'">
                    <template v-if="hoursSaved > 0">
                      You're saving approximately {{ hoursSaved }} hours/week!
                    </template>
                    <template v-else-if="hoursSaved < 0">
                      That's {{ Math.abs(hoursSaved) }} more hours than before.
                    </template>
                    <template v-else>
                      Same as before.
                    </template>
                  </p>
                </ValidationProvider>
              </div>
            </section>

            <!-- Pain Points Section -->
            <section v-if="originalPainPoints && originalPainPoints.length > 0" class="bg-gray-50 border border-gray-200 shadow-sm rounded-lg px-4 py-6 sm:p-6 lg:p-8">
              <h2 class="text-lg font-medium text-gray-900">Challenges</h2>

              <div class="mt-4 bg-white border border-gray-200 rounded-lg p-4">
                <p class="text-sm text-gray-500 mb-2">When you signed up, these were your biggest challenges:</p>
                <ul class="list-disc list-inside text-gray-700 text-sm space-y-1">
                  <li v-for="(point, index) in originalPainPoints" :key="index">{{ point }}</li>
                </ul>
              </div>

              <div class="mt-6">
                <label class="block text-sm font-medium text-gray-700 mb-3">
                  Which of these has WrestlingIQ helped with?
                </label>
                <div class="space-y-2">
                  <div
                    v-for="(point, index) in originalPainPoints"
                    :key="index"
                    class="relative flex items-start p-3 bg-white border border-gray-200 rounded-lg"
                    :class="{ 'border-wiq-green bg-green-50': form.painPointsResolved.includes(point) }"
                  >
                    <div class="flex items-center h-5">
                      <input
                        :id="`pain-point-${index}`"
                        :data-cy="`pain-point-${index}`"
                        type="checkbox"
                        :value="point"
                        v-model="form.painPointsResolved"
                        class="h-4 w-4 text-wiq-green border-gray-300 rounded focus:ring-wiq-green"
                      >
                    </div>
                    <div class="ml-3 text-sm">
                      <label :for="`pain-point-${index}`" class="text-gray-700 cursor-pointer">{{ point }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-6">
                <label for="challenge-notes" class="block text-sm font-medium text-gray-700">
                  What's the biggest thing we could help you with right now?
                </label>
                <div class="mt-1">
                  <textarea
                    id="challenge-notes"
                    data-cy="challenge-notes"
                    v-model="form.challengeNotes"
                    rows="3"
                    :class="inputClasses()"
                    placeholder="Could be a feature, a question about running your program, or anything else."
                  ></textarea>
                </div>
              </div>
            </section>

            <!-- NPS Section -->
            <section class="bg-gray-50 border border-gray-200 shadow-sm rounded-lg px-4 py-6 sm:p-6 lg:p-8">
              <h2 class="text-lg font-medium text-gray-900">Spread the Word</h2>

              <div class="mt-6">
                <ValidationProvider name="NPS score" rules="required" v-slot="{ errors }">
                  <label class="block text-sm font-medium text-gray-700 mb-3">
                    How likely are you to recommend WrestlingIQ to another program?
                  </label>
                  <div class="grid grid-cols-6 sm:grid-cols-11 gap-1.5 sm:gap-2">
                    <label v-for="n in 11" :key="n - 1" :data-cy="'nps-score-' + (n - 1)" class="cursor-pointer">
                      <input
                        type="radio"
                        v-model="form.npsScore"
                        :value="n - 1"
                        class="sr-only peer"
                      >
                      <span
                        class="aspect-square rounded-lg border-2 border-gray-300 flex items-center justify-center text-gray-700 text-sm font-medium peer-checked:border-wiq-green peer-checked:bg-wiq-green peer-checked:text-white transition-colors"
                      >
                        {{ n - 1 }}
                      </span>
                    </label>
                  </div>
                  <div class="flex justify-between text-xs text-gray-500 mt-2">
                    <span>0 = Not at all likely</span>
                    <span>10 = Extremely likely</span>
                  </div>
                  <p class="mt-2 text-sm text-red-600" v-if="errors[0]">{{ errors[0] }}</p>
                </ValidationProvider>
              </div>

              <div class="mt-6">
                <label for="nps-feedback" class="block text-sm font-medium text-gray-700">
                  What's the main reason for your score?
                </label>
                <div class="mt-1">
                  <textarea
                    id="nps-feedback"
                    data-cy="nps-feedback"
                    v-model="form.npsFeedback"
                    rows="3"
                    :class="inputClasses()"
                    placeholder="Tell us more..."
                  ></textarea>
                </div>
              </div>
            </section>

            <!-- Anything Else Section -->
            <section class="bg-gray-50 border border-gray-200 shadow-sm rounded-lg px-4 py-6 sm:p-6 lg:p-8">
              <h2 class="text-lg font-medium text-gray-900">Anything Else?</h2>

              <div class="mt-6">
                <label for="anything-else" class="block text-sm font-medium text-gray-700">
                  Is there anything else you'd like us to know?
                </label>
                <div class="mt-1">
                  <textarea
                    id="anything-else"
                    data-cy="anything-else"
                    v-model="form.anythingElse"
                    rows="3"
                    :class="inputClasses()"
                    placeholder="Any other feedback, suggestions, or comments..."
                  ></textarea>
                </div>
              </div>
            </section>

            <!-- Submit Button -->
            <div class="mt-4">
              <button
                type="submit"
                @click.prevent="handleSubmit(submitSurvey)"
                :disabled="submitting"
                data-cy="submit-survey"
                class="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-wiq-green hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Check-In
                <spinner v-if="submitting" class="ml-2"></spinner>
              </button>
              <p v-if="error" class="text-red-600 mt-2 text-center">
                {{ errorMessage }}
              </p>
              <small v-if="failed" class="text-red-600 mt-2 block text-center">
                Please correct the errors above before submitting
              </small>
            </div>

          </form>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import ErrorIcon from './icons/error-icon.vue';
import Spinner from './icons/spinner.vue';

export default {
  name: 'post-activation-survey',
  components: {
    ValidationProvider,
    ValidationObserver,
    ErrorIcon,
    Spinner,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
    surveyType: {
      type: String,
      required: true,
    },
    teamName: {
      type: String,
      default: '',
    },
    originalGoal: {
      type: String,
      default: null,
    },
    originalAdminHours: {
      type: Number,
      default: null,
    },
    originalPainPoints: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        goalAchieved: null,
        goalProgress: null,
        goalNotes: '',
        npsScore: null,
        npsFeedback: '',
        adminHoursNow: null,
        painPointsResolved: [],
        challengeNotes: '',
        expansionInterests: [],
        anythingElse: '',
      },
      goalOptions: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
      expansionOptions: [
        'Messaging',
        'Calendar',
        'Parent accounts',
        'Practice plans',
        'Attendance tracking',
        'Video & stats',
        'Rosters',
        'Documents',
        'Private lessons',
        'Payments',
        'Online registration',
        'Team website',
        'Fundraisers',
        'Donations',
        'Private liability insurance',
        'Additional team/location',
      ],
      submitting: false,
      error: false,
      errorMessage: '',
    };
  },
  computed: {
    surveyTypeLabel() {
      return this.surveyType === '90_day' ? 'a few months' : 'about a year';
    },
    hoursSaved() {
      if (this.originalAdminHours === null || !this.form.adminHoursNow) {
        return null;
      }
      return this.originalAdminHours - Number(this.form.adminHoursNow);
    },
  },
  methods: {
    inputClasses(error) {
      const normalClasses = 'py-3 px-4 shadow-sm block w-full border-gray-300 rounded-md sm:text-sm';
      if (!error) {
        return `${normalClasses} focus:ring-wiq-green focus:border-wiq-green border-gray-300`;
      }
      return `border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:ring-red ${normalClasses}`;
    },
    async submitSurvey() {
      if (this.submitting) {
        return;
      }

      this.submitting = true;
      this.error = false;
      this.errorMessage = '';

      const params = {
        post_activation_survey: {
          goal_achieved: this.form.goalAchieved,
          goal_progress: this.form.goalProgress,
          goal_notes: this.form.goalNotes,
          nps_score: this.form.npsScore,
          nps_feedback: this.form.npsFeedback,
          admin_hours_now: this.form.adminHoursNow,
          pain_points_resolved: this.form.painPointsResolved,
          challenge_notes: this.form.challengeNotes,
          expansion_interests: this.form.expansionInterests,
          anything_else: this.form.anythingElse,
        },
      };

      try {
        await axios.patch(`/wiq-surveys/${this.token}`, params);
        window.location.assign(`/wiq-surveys/${this.token}/thank_you`);
      } catch (err) {
        this.submitting = false;
        this.error = true;
        if (err.response && err.response.data && err.response.data.errors) {
          this.errorMessage = err.response.data.errors.join(', ');
        } else {
          this.errorMessage = 'An error occurred. Please try again or contact support@wrestlingiq.com';
        }
      }
    },
  },
};
</script>
